import { useOpsStore } from '../../store/ops.store';
import type { NegativeFeedback } from '../../types/ops';
import { AlertTriangle, Download } from 'lucide-react';
import { format, parseISO } from 'date-fns';

function ratingCell(value: number, isDark: boolean) {
  const isNegative = value <= 2;
  return (
    <td
      className={`px-3 py-2.5 text-center ${
        isNegative
          ? isDark ? 'bg-red-900/40 text-red-300 font-semibold' : 'bg-red-50 text-red-600 font-semibold'
          : isDark ? 'text-slate-300' : 'text-gray-600'
      }`}
    >
      {value} ⭐
    </td>
  );
}

function formatDate(dateStr: string): string {
  try {
    return format(parseISO(dateStr), 'dd/MM/yyyy HH:mm');
  } catch {
    return dateStr;
  }
}

export default function NegativeFeedbackTable({ isDark }: { isDark: boolean }) {
  const negativeFeedback = useOpsStore((s) => s.negativeFeedback);

  if (negativeFeedback.length === 0) {
    return (
      <div className={`rounded-lg p-6 text-center ${isDark ? 'bg-slate-800 text-slate-400' : 'bg-white text-gray-500 border border-gray-200 shadow-sm'}`}>
        Không có phản hồi tiêu cực trong giai đoạn này.
      </div>
    );
  }

  const exportCsv = () => {
    const header = ['Khách hàng', 'SĐT', 'Chương trình', 'Studio', 'Ngày', 'Coach', 'Studio Rating', 'TP', 'Nhận xét'];
    const csvRows = [header.join(',')];
    for (const row of negativeFeedback) {
      csvRows.push([
        `"${row.memberName.replace(/"/g, '""')}"`,
        `"${row.phoneNumber}"`,
        `"${row.programName.replace(/"/g, '""')}"`,
        `"${row.studioName.replace(/"/g, '""')}"`,
        `"${formatDate(row.startTime)}"`,
        row.coachRating, row.studioRating, row.tpRating,
        `"${(row.comment || '').replace(/"/g, '""')}"`,
      ].join(','));
    }
    const blob = new Blob(['\uFEFF' + csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'negative_feedback.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className={`rounded-lg p-3 sm:p-4 ${isDark ? 'bg-slate-800' : 'bg-white border border-gray-200 shadow-sm'}`}>
      <div className="flex items-center gap-2 mb-3 sm:mb-4">
        <AlertTriangle className="h-5 w-5 text-red-400" />
        <h3 className={`text-base sm:text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Phản hồi tiêu cực ({negativeFeedback.length})
        </h3>
        <div className="flex-1" />
        <button
          onClick={exportCsv}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
            isDark
              ? 'bg-white/5 text-slate-300 hover:bg-white/10 hover:text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900'
          }`}
        >
          <Download className="w-3.5 h-3.5" />
          Export CSV
        </button>
      </div>
      <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
        <table className="w-full text-sm text-left">
          <thead className={`sticky top-0 z-10 ${isDark ? 'bg-slate-800' : 'bg-white'}`}>
            <tr className={`border-b ${isDark ? 'border-slate-700 text-slate-300' : 'border-gray-200 text-gray-600'}`}>
              <th className={`px-3 py-3 font-medium whitespace-nowrap sticky left-0 z-20 ${isDark ? 'bg-slate-800' : 'bg-white'}`}>Khách hàng</th>
              <th className="px-3 py-3 font-medium whitespace-nowrap">SĐT</th>
              <th className="px-3 py-3 font-medium whitespace-nowrap">Chương trình</th>
              <th className="px-3 py-3 font-medium whitespace-nowrap">Studio</th>
              <th className="px-3 py-3 font-medium whitespace-nowrap">Ngày</th>
              <th className="px-3 py-3 font-medium whitespace-nowrap text-center">Coach</th>
              <th className="px-3 py-3 font-medium whitespace-nowrap text-center">Studio</th>
              <th className="px-3 py-3 font-medium whitespace-nowrap text-center">TP</th>
              <th className="px-3 py-3 font-medium whitespace-nowrap">Nhận xét</th>
            </tr>
          </thead>
          <tbody>
            {negativeFeedback.map((row: NegativeFeedback, idx: number) => (
              <tr
                key={row.bookingId}
                className={`border-b ${isDark ? 'border-slate-700/50' : 'border-gray-100'} ${
                  idx % 2 === 0
                    ? isDark ? 'bg-slate-800' : 'bg-white'
                    : isDark ? 'bg-slate-900' : 'bg-gray-50'
                } ${isDark ? 'hover:bg-slate-700/50' : 'hover:bg-gray-100'} transition-colors`}
              >
                <td className={`px-3 py-2.5 whitespace-nowrap sticky left-0 z-10 ${isDark ? 'text-white' : 'text-gray-900'} ${
                  idx % 2 === 0
                    ? isDark ? 'bg-slate-800' : 'bg-white'
                    : isDark ? 'bg-slate-900' : 'bg-gray-50'
                }`}>
                  {row.memberName}
                </td>
                <td className={`px-3 py-2.5 whitespace-nowrap ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>
                  {row.phoneNumber}
                </td>
                <td className={`px-3 py-2.5 whitespace-nowrap ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>
                  {row.programName}
                </td>
                <td className={`px-3 py-2.5 whitespace-nowrap ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>
                  {row.studioName}
                </td>
                <td className={`px-3 py-2.5 whitespace-nowrap ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>
                  {formatDate(row.startTime)}
                </td>
                {ratingCell(row.coachRating, isDark)}
                {ratingCell(row.studioRating, isDark)}
                {ratingCell(row.tpRating, isDark)}
                <td className={`px-3 py-2.5 whitespace-nowrap ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>
                  {row.comment}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
